import React, { useEffect, useState } from 'react';
import { TIKTOK_OAUTH_CALLBACK_URL } from '../../utils/constants';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../../components/ui/card';

const normalizeBaseUrl = (url) => (url || '').trim().replace(/\/+$/, '');

const buildCallbackUrl = (code, state, clientId) => {
  const callbackBaseUrl = normalizeBaseUrl(TIKTOK_OAUTH_CALLBACK_URL);
  if (!callbackBaseUrl) {
    return null;
  }

  return {
    baseUrl: callbackBaseUrl,
    payload: {
      code,
      state,
      clientId: clientId || undefined
    }
  };
};

const extractClientIdFromState = (state) => {
  // 1. sessionStorage — most reliable, saved in Links.js before the OAuth redirect
  try {
    const stored = sessionStorage.getItem('tiktok_oauth_clientId');
    if (stored) {
      sessionStorage.removeItem('tiktok_oauth_clientId');
      return stored;
    }
  } catch {
    // sessionStorage unavailable
  }

  // 2. Try URL-decoded then JSON-parsed state: {"clientId":"...","nonce":"...","issuedAt":...}
  try {
    const stateData = JSON.parse(decodeURIComponent(state));
    if (stateData?.clientId) return stateData.clientId;
  } catch {
    // Not URL-encoded JSON
  }

  // 3. Try raw JSON parse (state already decoded by browser)
  try {
    const stateData = JSON.parse(state);
    if (stateData?.clientId) return stateData.clientId;
  } catch {
    // Not raw JSON
  }

  // 4. Try base64-decoded JSON (common in OAuth state params)
  try {
    const decoded = atob(state.replace(/-/g, '+').replace(/_/g, '/'));
    const stateData = JSON.parse(decoded);
    if (stateData?.clientId) return stateData.clientId;
  } catch {
    // Not base64 JSON
  }

  return null;
};

const TikTokCallback = () => {
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing TikTok authorization...');
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    console.log('TikTokCallback mounted:', window.location.href);

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    if (error) {
      setStatus('error');
      setMessage(errorDescription || error);
      return;
    }

    if (!code || !state) {
      setStatus('error');
      setMessage('Missing authorization data from TikTok.');
      return;
    }

    const clientId = extractClientIdFromState(state);
    const nextUrl = buildCallbackUrl(code, state, clientId);
    const debugPayload = {
      incomingUrl: window.location.href,
      callbackEnv: TIKTOK_OAUTH_CALLBACK_URL,
      clientId,
      callbackPayload: nextUrl?.payload,
      timestamp: new Date().toISOString()
    };

    setDebugInfo(debugPayload);

    try {
      localStorage.setItem(
        'tiktok_callback_debug',
        JSON.stringify(debugPayload, null, 2)
      );
    } catch {
      // ignore storage issues
    }

    if (!nextUrl) {
      setStatus('error');
      setMessage(
        'TikTok backend callback URL is not configured. Check TIKTOK_OAUTH_CALLBACK_URL.'
      );
      return;
    }

    console.log('TIKTOK_OAUTH_CALLBACK_URL:', TIKTOK_OAUTH_CALLBACK_URL);
    console.log('TikTok backend callback payload:', nextUrl.payload);

    setMessage('TikTok authorized. Redirecting to backend callback...');

    // Use POST with credentials instead of GET redirect
    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await fetch(nextUrl.baseUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include', // Send cookies with request
          body: JSON.stringify(nextUrl.payload)
        });

        if (!response.ok) {
          throw new Error(`Backend returned ${response.status}`);
        }

        const data = await response.json();

        // Redirect to the URL provided by backend (usually dashboard or success page)
        if (data.redirectUrl) {
          window.location.replace(data.redirectUrl);
        } else {
          setStatus('success');
          setMessage('TikTok account connected successfully!');
        }
      } catch (err) {
        console.error('Callback error:', err);
        setStatus('error');
        setMessage(`Connection failed: ${err.message}`);
      }
    }, 1200);

    return () => window.clearTimeout(timeoutId);
  }, []);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>
            {status === 'processing' && 'Connecting TikTok...'}
            {status === 'success' && 'Success!'}
            {status === 'error' && 'Connection Failed'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center gap-4">
            {status === 'processing' && (
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
            )}
            {status === 'success' && (
              <div className="text-green-600 text-5xl">OK</div>
            )}
            {status === 'error' && (
              <div className="text-red-600 text-5xl">X</div>
            )}
            <p className="text-center text-sm text-gray-600">{message}</p>
            {debugInfo && (
              <div className="w-full rounded-lg bg-gray-100 p-3 text-left text-xs text-gray-700 break-all">
                <p>
                  <strong>Callback env:</strong> {debugInfo.callbackEnv}
                </p>
                <p className="mt-2">
                  <strong>Client ID:</strong> {debugInfo.clientId}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TikTokCallback;
